import * as React from "react";
import classNames from "classnames";
import ResponsiveImage from "../../responsive-image/responsive-image";
import Title from "~/components/title/title";
import CtaButton from "../../cta-button/cta-button";
import { HorizontalAlignmentType } from "~/types/alignment";
import ArrowLink from "../../arrow-link/arrow-link";
import { ThemeColorType } from "~/types/theme-color";
import WrapperLink from "~/components/wrapper-link/wrapper-link";
import VideoPlayer from "~/components/video-player/video-player";

export interface ImageTextCtaSliderItemProps {
    imageSrc?: string;
    imageAltText?: string;
    imageLinkUrl?: string;
    imageLinkTarget?: string;
    isTitleH1?: boolean;
    title?: string;
    richText?: string;
    textColor?: ThemeColorType;
    textAlignContent?: HorizontalAlignmentType;
    ctaLink?: string;
    showCtaAsLink?: boolean;
    ctaBgColor?: ThemeColorType;
    ctaTextColor?: ThemeColorType;
    horizontalAlignContent?: HorizontalAlignmentType;
    navigationColor?: ThemeColorType;
    bgColor?: ThemeColorType;
    videoUrl?: string;
    index?: number;
    activeSlideIndex?: number;
    wasActiveSlideIndex?: number;
}

const ImageTextCtaSliderItem = ({
    imageSrc,
    imageAltText,
    imageLinkUrl,
    imageLinkTarget,
    isTitleH1,
    title,
    richText,
    ctaLink,
    showCtaAsLink,
    horizontalAlignContent = HorizontalAlignmentType.CENTER,
    textAlignContent = HorizontalAlignmentType.CENTER,
    bgColor,
    textColor = ThemeColorType.BLACK,
    ctaBgColor = ThemeColorType.BLACK,
    ctaTextColor = ThemeColorType.WHITE,
    videoUrl,
    index,
    activeSlideIndex,
    wasActiveSlideIndex
}: ImageTextCtaSliderItemProps) => {
    const [playing, setPlaying] = React.useState(false);

    const hasContent = title || richText || ctaLink;
    const baseClass = "image-text-cta-slider-item";
    const componentClasses = classNames(
        baseClass,
        `horizontal-align-${horizontalAlignContent}`,
        `text-align-${textAlignContent}`,
        `theme-color-${textColor}`,
        {
            [`theme-bg-${bgColor}`]: !imageLinkUrl && !!bgColor,
            [`${baseClass}--has-no-content`]: !hasContent
        }
    );
    const contentClasses = classNames(`${baseClass}__content-wrapper`);
    const buttonClasses = classNames(
        `${baseClass}__button`,
        `theme-bg-${ctaBgColor}`,
        `theme-color-${ctaTextColor}`
    );
    const arrowLinkClasses = classNames(`${baseClass}__link`, `theme-color-${ctaTextColor}`);

    React.useEffect(() => {
        if (index === activeSlideIndex) {
            setPlaying(true);
        }
    }, [activeSlideIndex]);
    React.useEffect(() => {
        if (index === wasActiveSlideIndex && index !== activeSlideIndex) {
            setPlaying(false);
        }
    }, [wasActiveSlideIndex]);

    return (
        <WrapperLink linkClassName={`${baseClass}-wrapper-link`} href={imageLinkUrl} target={imageLinkTarget}>
            <div className={componentClasses}>
                {!videoUrl && imageSrc && (
                    <ResponsiveImage
                        className={`${baseClass}__image`}
                        defaultSize={1366}
                        srcSetSizes={[320, 375, 480, 640, 768, 1024, 1280, 1366]}
                        sizes="(max-width: 1365px) 100vw, 1366px"
                        alt={imageAltText}
                        srcUrl={imageSrc}
                    />
                )}

                {videoUrl && (
                    <VideoPlayer url={videoUrl} isPlaying={playing} autoMute loop hideNativePlayBtn />
                )}

                {hasContent && (
                    <div className={contentClasses}>
                        {title && <Title title={title} isTitleH1={isTitleH1} />}
                        {richText && (
                            <div
                                className={`${baseClass}__rich-text content-rich-text`}
                                dangerouslySetInnerHTML={{ __html: richText }}
                            />
                        )}
                        {ctaLink && (
                            <>
                                {showCtaAsLink ? (
                                    <ArrowLink className={arrowLinkClasses} link={ctaLink} />
                                ) : (
                                    <CtaButton className={buttonClasses} link={ctaLink} isLarge />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </WrapperLink>
    );
};

export default ImageTextCtaSliderItem;
